import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FiUser, FiSmartphone, FiBell, FiX, FiCheck, FiChevronRight, FiDownload
} from 'react-icons/fi';
import { useAuthStore } from '../store/authStore';
import './WelcomeFlow.css';

const API = '/api';
const DAY = 24 * 60 * 60 * 1000;
const DISMISS_KEY = {
  login: 'iw-onboarding-login-until',
  install: 'iw-onboarding-install-until',
  push: 'iw-onboarding-push-until',
};
const INSTALL_DONE_KEY = 'iw-onboarding-installed';
const INSTALL_REWARD_KEY = 'iw-install-reward-done';
const LOGIN_HINT_KEY = 'iw-login-hint-pending';

function isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true
      || String(document.referrer || '').startsWith('android-app://');
  } catch (e) {
    return false;
  }
}

function isMobileDevice() {
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile|HarmonyOS|OpenHarmony|ArkWeb/i.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) return true;
  try {
    if (window.matchMedia('(pointer: coarse)').matches && navigator.maxTouchPoints > 0) return true;
  } catch (e) { /* 老浏览器忽略 */ }
  return false;
}

function isIOSDevice() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0;
}

function isWeChatBrowser() {
  return /MicroMessenger/i.test(navigator.userAgent || '');
}

function canPush() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

function isFormalUser(user) {
  if (!user) return false;
  if (user.isGuest === true || user.isGuestAccount === true) return false;
  if (user.is_guest === 1 || user.is_guest === true) return false;
  return true;
}

function isGuestUser(user) {
  return Boolean(user) && !isFormalUser(user);
}

function b64ToU8(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

function swReady() {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('service worker 还没准备好')), 6000);
    }),
  ]);
}

async function subscribeAndReport(getHeaders) {
  const keyRes = await axios.get(`${API}/push/key`);
  if (!keyRes.data?.enabled || !keyRes.data?.publicKey) throw new Error('推送服务还没就绪');
  const reg = await swReady();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToU8(keyRes.data.publicKey),
    });
  }
  await axios.post(`${API}/push/subscribe`, sub.toJSON(), { headers: getHeaders() });
  return sub;
}

function readDismissed(key) {
  try {
    return Number(localStorage.getItem(key) || 0);
  } catch (e) {
    return 0;
  }
}

export default function WelcomeFlow() {
  const { user, getHeaders } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [panel, setPanel] = useState(null);
  const [standalone, setStandalone] = useState(isStandalone);
  const [installed, setInstalled] = useState(() => {
    try {
      return localStorage.getItem(INSTALL_DONE_KEY) === '1'
        || localStorage.getItem(INSTALL_REWARD_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const [installPrompt, setInstallPrompt] = useState(() => Boolean(window.__iwInstallPrompt));
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [pushSupported, setPushSupported] = useState(() => canPush());
  const [pushPermission, setPushPermission] = useState(() => {
    try { return canPush() ? Notification.permission : 'unsupported'; } catch (e) { return 'unsupported'; }
  });
  const [pushReady, setPushReady] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [loginHint, setLoginHint] = useState(false);
  const [loginHintPos, setLoginHintPos] = useState(null);
  const panelRef = useRef(null);
  const mutedRef = useRef(new Set());
  const [mobile] = useState(isMobileDevice);
  const [ios] = useState(isIOSDevice);
  const [wechat] = useState(isWeChatBrowser);

  const formal = isFormalUser(user);
  const guest = isGuestUser(user);
  const installDone = !mobile || standalone || installed;
  const pushDenied = pushPermission === 'denied';
  const path = location.pathname;
  const suppressed = path.startsWith('/login')
    || path.startsWith('/register')
    || path.startsWith('/shared')
    || path.startsWith('/maps/edit/')
    || path.startsWith('/maps/new')
    || path.startsWith('/interactive/edit/')
    || path.startsWith('/interactive/new')
    || path.startsWith('/settings');

  const nextStep = (() => {
    if (!formal) return 'login';
    if (mobile && !installDone) return 'install';
    if (pushSupported && !pushReady && !pushDenied && (standalone || !mobile)) return 'push';
    return null;
  })();

  const setOpenPanel = useCallback((stage) => {
    panelRef.current = stage;
    setPanel(stage);
    if (stage === 'install') {
      setShowInstallHelp(!window.__iwInstallPrompt || ios || wechat);
    }
  }, [ios, wechat]);

  const dismiss = useCallback((stage, days) => {
    if (DISMISS_KEY[stage]) {
      try {
        localStorage.setItem(DISMISS_KEY[stage], String(Date.now() + days * DAY));
      } catch (e) { /* 本地存储不可用时只影响本次 */ }
    }
    mutedRef.current.add(stage);
    panelRef.current = null;
    setPanel(null);
  }, []);

  const hideLoginHint = useCallback(() => {
    setLoginHint(false);
    setLoginHintPos(null);
    try { localStorage.removeItem(LOGIN_HINT_KEY); } catch (e) { /* 忽略 */ }
  }, []);

  const updateLoginHintPosition = useCallback(() => {
    const candidates = [
      ...document.querySelectorAll('.nav-login-link'),
      ...document.querySelectorAll('.mobile-menu-btn'),
    ];
    const target = candidates.find(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).visibility !== 'hidden';
    });
    if (!target) return false;
    const rect = target.getBoundingClientRect();
    const hintWidth = 168;
    const hintHeight = 44;
    const gap = 12;
    let left = rect.left - hintWidth - gap;
    let top = rect.top + rect.height / 2 - hintHeight / 2;
    let placement = 'left';
    if (left < 8) {
      placement = 'below';
      left = Math.min(window.innerWidth - hintWidth - 8, Math.max(8, rect.right - hintWidth));
      top = rect.bottom + gap;
    }
    let radius = 8;
    try { radius = parseFloat(window.getComputedStyle(target).borderRadius) || 8; } catch (e) { /* 忽略 */ }
    setLoginHintPos({
      top: Math.max(6, top),
      left: Math.max(8, left),
      placement,
      ringTop: rect.top - 4,
      ringLeft: rect.left - 4,
      ringWidth: rect.width + 8,
      ringHeight: rect.height + 8,
      ringRadius: radius + 3,
    });
    return true;
  }, []);

  // 独立窗口启动：记录已添加，并发放一次安装奖励。
  useEffect(() => {
    const syncDisplayMode = () => {
      const next = isStandalone();
      setStandalone(next);
      if (next) {
        setInstalled(true);
        try { localStorage.setItem(INSTALL_DONE_KEY, '1'); } catch (e) { /* 忽略 */ }
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      try { localStorage.setItem(INSTALL_DONE_KEY, '1'); } catch (e) { /* 忽略 */ }
    };
    syncDisplayMode();
    window.addEventListener('appinstalled', onInstalled);
    let mql;
    try {
      mql = window.matchMedia('(display-mode: standalone)');
      if (mql.addEventListener) mql.addEventListener('change', syncDisplayMode);
      else if (mql.addListener) mql.addListener(syncDisplayMode);
    } catch (e) { /* 忽略 */ }
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      try {
        if (mql?.removeEventListener) mql.removeEventListener('change', syncDisplayMode);
        else if (mql?.removeListener) mql.removeListener(syncDisplayMode);
      } catch (e) { /* 忽略 */ }
    };
  }, []);

  // 捕获浏览器提供的安装事件；没有这个事件时展示手动添加步骤。
  useEffect(() => {
    const sync = () => setInstallPrompt(Boolean(window.__iwInstallPrompt));
    sync();
    window.addEventListener('iw-install-available', sync);
    return () => window.removeEventListener('iw-install-available', sync);
  }, []);

  // 同步通知权限与真实订阅状态，避免“已授权但没订阅”被当成完成。
  useEffect(() => {
    let alive = true;
    const syncPush = async () => {
      if (!canPush()) {
        if (alive) {
          setPushSupported(false);
          setPushPermission('unsupported');
          setPushReady(false);
        }
        return;
      }
      const permission = Notification.permission;
      if (alive) {
        setPushSupported(true);
        setPushPermission(permission);
      }
      if (permission !== 'granted') {
        if (alive) setPushReady(false);
        return;
      }
      try {
        const reg = await swReady();
        let sub = await reg.pushManager.getSubscription();
        if (!sub && formal && (standalone || !mobile)) {
          sub = await subscribeAndReport(getHeaders);
        }
        if (alive) setPushReady(Boolean(sub));
      } catch (e) {
        if (alive) setPushReady(false);
      }
    };
    syncPush();
    window.addEventListener('focus', syncPush);
    return () => {
      alive = false;
      window.removeEventListener('focus', syncPush);
    };
  }, [user?.id, formal, standalone, mobile, getHeaders]);

  // 安装奖励只在正式账号、手机独立窗口里发一次。
  useEffect(() => {
    if (!standalone || !formal || !mobile) return undefined;
    try {
      if (localStorage.getItem(INSTALL_REWARD_KEY)) return undefined;
    } catch (e) { /* 继续尝试，服务端仍会去重 */ }
    let alive = true;
    axios.post(`${API}/profile/install-reward`, {}, { headers: getHeaders() })
      .then(res => {
        if (!alive) return;
        try { localStorage.setItem(INSTALL_REWARD_KEY, '1'); } catch (e) { /* 忽略 */ }
        if (res.data?.ok && !res.data.already && res.data.gained) {
          toast.success(`从桌面进入，奖励 +${res.data.gained} 经验`, { duration: 6000 });
        }
      })
      .catch(() => { /* 离线或接口异常时，下次进入再试 */ });
    return () => { alive = false; };
  }, [standalone, formal, mobile, user?.id, getHeaders]);

  // 菜单里的“新手引导”随时可以打开，不受自动弹窗冷却影响。
  useEffect(() => {
    const openChecklist = () => setOpenPanel('checklist');
    window.addEventListener('iw:onboarding-open', openChecklist);
    return () => window.removeEventListener('iw:onboarding-open', openChecklist);
  }, [setOpenPanel]);

  // 关闭登录弹窗后保留一个指向登录按钮的提示，直到点空白或去登录。
  useEffect(() => {
    if (user || suppressed) return;
    let pending = false;
    try { pending = localStorage.getItem(LOGIN_HINT_KEY) === '1'; } catch (e) { pending = false; }
    if (pending) setLoginHint(true);
  }, [user?.id, suppressed]);

  useEffect(() => {
    if (user) {
      setLoginHint(false);
      setLoginHintPos(null);
      try { localStorage.removeItem(LOGIN_HINT_KEY); } catch (e) { /* 忽略 */ }
    }
  }, [user?.id, user]);

  useEffect(() => {
    if (!loginHint || panel) return undefined;
    let timer;
    const tick = () => {
      if (updateLoginHintPosition()) return;
      timer = setTimeout(tick, 200);
    };
    tick();
    window.addEventListener('resize', updateLoginHintPosition);
    window.addEventListener('scroll', updateLoginHintPosition, true);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', updateLoginHintPosition);
      window.removeEventListener('scroll', updateLoginHintPosition, true);
    };
  }, [loginHint, panel, updateLoginHintPosition]);

  useEffect(() => {
    const active = Boolean(loginHint && !panel && loginHintPos);
    document.body.classList.toggle('iw-login-hint-active', active);
    return () => document.body.classList.remove('iw-login-hint-active');
  }, [loginHint, panel, loginHintPos]);

  useEffect(() => {
    if (path.startsWith('/login')) hideLoginHint();
  }, [path, hideLoginHint]);

  // 首次访问立即询问账号；安装和通知只在前一步完成后出现。
  useEffect(() => {
    if (suppressed || !nextStep) return undefined;
    if (mutedRef.current.has(nextStep)) return undefined;

    const force = new URLSearchParams(location.search).get('onboarding');
    const forced = force === '1'
      || force === nextStep
      || (force === 'install' && nextStep === 'install')
      || (force === 'push' && nextStep === 'push')
      || (force === 'login' && nextStep === 'login')
      || (new URLSearchParams(location.search).get('installGuide') === '1' && nextStep === 'install')
      || (new URLSearchParams(location.search).get('pushPrompt') === '1' && nextStep === 'push');

    if (!forced && Date.now() < readDismissed(DISMISS_KEY[nextStep])) return undefined;

    let timer;
    const show = () => {
      if (panelRef.current) return;
      if (document.hidden || document.querySelector('.gd-game-fullscreen')) {
        timer = setTimeout(show, 15000);
        return;
      }
      setOpenPanel(nextStep);
    };
    timer = setTimeout(show, forced ? 250 : nextStep === 'login' ? 300 : 2600);
    return () => clearTimeout(timer);
  }, [nextStep, suppressed, location.search, setOpenPanel]);

  // 调试和运营链接：?onboarding=checklist 直接打开三件套。
  useEffect(() => {
    const force = new URLSearchParams(location.search).get('onboarding');
    if (force === 'checklist') setOpenPanel('checklist');
  }, [location.search, setOpenPanel]);

  useEffect(() => {
    if (!panel) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [panel]);

  const handleAccount = () => {
    const redirect = encodeURIComponent(location.pathname + location.search);
    hideLoginHint();
    dismiss('login', 3);
    if (guest) navigate('/settings?tab=security');
    else navigate(`/login?redirect=${redirect}`);
  };

  const handleInstall = async () => {
    const event = window.__iwInstallPrompt;
    if (!event) {
      setShowInstallHelp(true);
      return;
    }
    setInstallBusy(true);
    try {
      event.prompt();
      const choice = await event.userChoice;
      window.__iwInstallPrompt = null;
      setInstallPrompt(false);
      if (choice?.outcome === 'accepted') {
        try { localStorage.setItem(INSTALL_DONE_KEY, '1'); } catch (e) { /* 忽略 */ }
        setInstalled(true);
        toast.success('已添加到主屏幕，从桌面打开后可以领 200 经验');
        dismiss('install', 30);
      } else {
        dismiss('install', 14);
      }
    } catch (e) {
      setShowInstallHelp(true);
    } finally {
      setInstallBusy(false);
    }
  };

  const handleEnablePush = async () => {
    if (!canPush()) {
      toast.error('当前浏览器不支持消息提醒');
      return;
    }
    setPushBusy(true);
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') {
        if (permission === 'denied') {
          toast.error('系统没有允许通知，需要到浏览器或手机设置里打开');
        } else {
          toast.error('还没有开启，可以再点一次');
        }
        return;
      }
      await subscribeAndReport(getHeaders);
      setPushReady(true);
      toast.success('已开启，有新消息时会出现在通知栏');
      axios.post(`${API}/push/test`, {}, { headers: getHeaders() }).catch(() => {});
      dismiss('push', 30);
    } catch (e) {
      toast.error(e?.response?.data?.error || '开启失败，稍后再试');
    } finally {
      setPushBusy(false);
    }
  };

  const handleLater = () => {
    if (panel === 'login') {
      dismiss('login', 3);
      if (!user) {
        try { localStorage.setItem(LOGIN_HINT_KEY, '1'); } catch (e) { /* 忽略 */ }
        setLoginHint(true);
      }
    } else if (panel === 'install') dismiss('install', 14);
    else if (panel === 'push') dismiss('push', pushDenied ? 30 : 7);
    else {
      panelRef.current = null;
      setPanel(null);
    }
  };

  const steps = [
    {
      key: 'account',
      title: '登录账号',
      done: formal,
      desc: formal
        ? '已登录，进度和奖励会跟着账号走。'
        : guest
          ? '当前是试玩模式，绑定邮箱或手机号后进度才不会丢。'
          : '登录后可以跨设备保存进度、作品和奖励。',
      action: !formal ? (guest ? '去绑定' : '去登录') : null,
    },
    {
      key: 'install',
      title: '添加到主屏幕',
      done: installDone,
      desc: !mobile
        ? '电脑端不需要添加，手机浏览器可以把它装到桌面。'
        : installDone
          ? '已添加，下次从桌面图标一点就能打开。'
          : '装到桌面后像应用一样打开，也能接收消息提醒。',
      action: mobile && !installDone ? '去添加' : null,
    },
    {
      key: 'push',
      title: '开启消息提醒',
      done: pushReady,
      desc: !pushSupported
        ? '当前浏览器不支持系统通知，可以换成主屏幕应用再试。'
        : pushDenied
          ? '系统通知被关闭了，需要到浏览器或手机设置里重新允许。'
          : pushReady
            ? '已开启，有人私信你时会在通知栏提醒。'
            : '开启后，私信、互动和收益动态会出现在通知栏。',
      action: pushSupported && !pushReady && !pushDenied ? '去开启' : null,
    },
  ];

  if (!panel) {
    if (!loginHint || !loginHintPos || user) return null;
    return (
      <>
        <div className="login-hint-mask" onClick={hideLoginHint} aria-hidden="true" />
        <svg
          className="login-hint-ring"
          style={{
            top: loginHintPos.ringTop,
            left: loginHintPos.ringLeft,
            width: loginHintPos.ringWidth,
            height: loginHintPos.ringHeight,
          }}
          viewBox={`0 0 ${loginHintPos.ringWidth} ${loginHintPos.ringHeight}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <rect
            x="1"
            y="1"
            width={Math.max(0, loginHintPos.ringWidth - 2)}
            height={Math.max(0, loginHintPos.ringHeight - 2)}
            rx={loginHintPos.ringRadius}
            ry={loginHintPos.ringRadius}
          />
        </svg>
        <div
          className={`login-hint is-${loginHintPos.placement}`}
          style={{ top: loginHintPos.top, left: loginHintPos.left }}
          role="status"
        >
          <span className="login-hint-text">在这里登录哦</span>
          <svg className="login-hint-arrow" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3 10h12M10 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </>
    );
  }

  const completed = steps.filter(step => step.done).length;
  const progress = Math.round((completed / steps.length) * 100);

  const renderInstallHelp = () => (
    <div className="wf-help">
      <div className="wf-help-title">添加方法</div>
      {wechat ? (
        <ol>
          <li>点右上角「…」</li>
          <li>选「在浏览器打开」</li>
          <li>在浏览器里点菜单，选「添加到主屏幕」</li>
        </ol>
      ) : ios ? (
        <ol>
          <li>用 Safari 打开本页</li>
          <li>点底部的「分享」按钮</li>
          <li>向下找到「添加到主屏幕」，再点「添加」</li>
        </ol>
      ) : (
        <ol>
          <li>点浏览器右上角或右下角的菜单</li>
          <li>找到「添加到主屏幕」或「安装应用」</li>
          <li>确认添加，之后从桌面图标进入</li>
        </ol>
      )}
      {!wechat && mobile && (
        <p className="wf-help-note">添加后第一次从桌面进入，正式账号可以领取 200 经验。</p>
      )}
    </div>
  );

  return (
    <div className="wf-mask" role="dialog" aria-modal="true" aria-label="新手引导" onClick={(e) => { if (e.target === e.currentTarget) handleLater(); }}>
      <div className="wf-card">
        <button type="button" className="wf-close" onClick={handleLater} aria-label="关闭"><FiX size={16} /></button>

        {panel === 'checklist' && (
          <>
            <div className="wf-brand">无限世界</div>
            <h2 className="wf-title">新手三件套</h2>
            <p className="wf-subtitle">登录、装到桌面、开启提醒，三步就能随时回来。</p>
            <div className="wf-progress-row">
              <div className="wf-progress"><span style={{ width: `${progress}%` }} /></div>
              <span className="wf-progress-text">{completed} / {steps.length}</span>
            </div>
            <div className="wf-checklist">
              {steps.map((step, index) => (
                <div key={step.key} className={`wf-check-item${step.done ? ' is-done' : ''}`}>
                  <div className="wf-check-icon">
                    {step.done ? <FiCheck size={16} /> : index === 0 ? <FiUser size={16} /> : index === 1 ? <FiSmartphone size={16} /> : <FiBell size={16} />}
                  </div>
                  <div className="wf-check-main">
                    <div className="wf-check-title">{step.title}</div>
                    <p>{step.desc}</p>
                  </div>
                  {step.action && (
                    <button
                      type="button"
                      className="wf-inline-action"
                      onClick={() => {
                        if (step.key === 'account') handleAccount();
                        else if (step.key === 'install') setOpenPanel('install');
                        else if (step.key === 'push') setOpenPanel('push');
                      }}
                    >
                      {step.action}<FiChevronRight size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="wf-secondary wf-full" onClick={handleLater}>关闭</button>
          </>
        )}

        {panel === 'login' && (
          <>
            <div className="wf-brand">无限世界</div>
            <div className="wf-icon"><FiUser size={22} /></div>
            <h2 className="wf-title">{guest ? '把试玩进度留下来' : '登录后，进度才会跟着你'}</h2>
            <p className="wf-subtitle">
              {guest
                ? '你现在是试玩模式，进度只保存在这台设备。绑定邮箱或手机号后，换设备、清缓存都不会丢。'
                : '登录后，玩过的作品、保存的进度和领到的奖励都会跟着账号走，换台手机也能继续。'}
            </p>
            <div className="wf-benefits">
              <span>跨设备保存</span>
              <span>作品与进度</span>
              <span>好友和奖励</span>
            </div>
            <button type="button" className="wf-primary wf-full" onClick={handleAccount}>
              {guest ? '绑定邮箱或手机号' : '登录 / 注册'}
            </button>
            <button type="button" className="wf-secondary wf-full" onClick={handleLater}>
              {guest ? '继续试玩' : '先看看，稍后再说'}
            </button>
            <p className="wf-footnote">完成后可以继续添加桌面入口和消息提醒。</p>
          </>
        )}

        {panel === 'install' && (
          <>
            <div className="wf-brand">无限世界</div>
            <div className="wf-icon"><FiSmartphone size={22} /></div>
            <h2 className="wf-title">装到桌面，下次一点就开</h2>
            <p className="wf-subtitle">添加到主屏幕后会像应用一样打开，也能接收私信和互动提醒。</p>
            {!installPrompt && renderInstallHelp()}
            {installPrompt && showInstallHelp && renderInstallHelp()}
            {installPrompt && !showInstallHelp && (
              <button type="button" className="wf-text-link" onClick={() => setShowInstallHelp(true)}>
                看不到按钮？查看手动添加方法
              </button>
            )}
            {installPrompt ? (
              <button type="button" className="wf-primary wf-full" onClick={handleInstall} disabled={installBusy}>
                <FiDownload size={16} /> {installBusy ? '正在打开…' : '添加到主屏幕'}
              </button>
            ) : (
              <button type="button" className="wf-primary wf-full" onClick={() => dismiss('install', 14)}>
                我知道了
              </button>
            )}
            {installPrompt && <button type="button" className="wf-secondary wf-full" onClick={handleLater}>稍后再说</button>}
          </>
        )}

        {panel === 'push' && (
          <>
            <div className="wf-brand">无限世界</div>
            <div className="wf-icon"><FiBell size={22} /></div>
            <h2 className="wf-title">{pushDenied ? '通知被系统关掉了' : '有人找你时，通知栏提醒'}</h2>
            <p className="wf-subtitle">
              {pushDenied
                ? '想让手机弹消息，需要到浏览器或手机「设置」里找到无限世界，把通知重新打开。'
                : !pushSupported
                  ? '当前浏览器不支持系统通知，可以先把网站添加到主屏幕，再从桌面应用里开启。'
                  : '开启后，私信、互动和收益动态会出现在通知栏，不用一直守着页面。'}
            </p>
            {!pushDenied && pushSupported && (
              <div className="wf-benefits">
                <span>私信提醒</span>
                <span>作品动态</span>
                <span>收益到账</span>
              </div>
            )}
            {!pushDenied && pushSupported ? (
              <button type="button" className="wf-primary wf-full" onClick={handleEnablePush} disabled={pushBusy}>
                {pushBusy ? '正在开启…' : '开启通知'}
              </button>
            ) : (
              <button type="button" className="wf-primary wf-full" onClick={handleLater}>知道了</button>
            )}
            {!pushDenied && pushSupported && (
              <button type="button" className="wf-secondary wf-full" onClick={handleLater}>暂不开启</button>
            )}
          </>
        )}

        {panel !== 'checklist' && (
          <div className="wf-steps-mini" aria-hidden="true">
            <span className={formal ? 'is-on' : ''} />
            <span className={installDone ? 'is-on' : ''} />
            <span className={pushReady ? 'is-on' : ''} />
          </div>
        )}
      </div>
    </div>
  );
}
